import { TenantFormattingService } from '../../../core/services/tenant-formatting.service';
import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FileImagePreviewComponent } from '../../components/file-image-preview/file-image-preview.component';
import { isPreviewableImage } from '../../../core/utils/image-file';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faDownload, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FilesService } from '../../../core/services/files.service';
import { FileModel } from '../../../core/models/file.model';
import { CreateFilesComponent } from './create-files/create-files.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner.component/loading-spinner.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FileSortOption } from '../../../core/models/shared/file-sort.model';
import { PaginatedList } from '../../../core/models/shared/paginated-list.model';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { SortDropdownComponent } from '../../components/sort-dropdown/sort-dropdown.component';
import { SortOptionBase } from '../../../core/models/shared/sort-option-base.model';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { NoItemsFoundComponent } from '../../components/no-items-found/no-items-found.component';
import { finalize } from 'rxjs';
import { ErrorAlertComponent } from '../../components/error-alert.component/error-alert.component';
import { getApiErrorMessage } from '../../../core/utils/api-error-message';
const FILE_SORT_OPTIONS: FileSortOption[] = [
  { label: 'Најнови', sortBy: 'DateCreated', sortDirection: 'Descending' },
  { label: 'Најстари', sortBy: 'DateCreated', sortDirection: 'Ascending' },
  { label: 'А - Ш', sortBy: 'FileName', sortDirection: 'Ascending' },
  { label: 'Ш - А', sortBy: 'FileName', sortDirection: 'Descending' }
];
@Component({
  selector: 'app-files',
  imports: [
    FileImagePreviewComponent,
    FaIconComponent,
    CreateFilesComponent,
    LoadingSpinnerComponent,
    MatPaginatorModule,
    SearchInputComponent,
    SortDropdownComponent,
    AppDatePipe,
    NoItemsFoundComponent,
    ErrorAlertComponent
  ],
  templateUrl: './files.component.html',
  styleUrl: './files.component.css'
})
export class FilesComponent implements OnInit {
  readonly formatting = inject(TenantFormattingService);
  readonly isPreviewableImage = isPreviewableImage;
  @ViewChild('createFileModal')
  createFileModal?: ElementRef<HTMLDivElement>;
  files: FileModel[] = [];
  filePage?: PaginatedList<FileModel>;
  loading = false;
  error = '';
  downloadingIds = new Set<string>();
  faDownload = faDownload;
  faPlus = faPlus;
  searchText = '';
  pageSize = 10;
  sortOptions = FILE_SORT_OPTIONS;
  selectedSort = FILE_SORT_OPTIONS[0];
  constructor(private filesService: FilesService) { }
  ngOnInit(): void {
    this.loadFiles();
  }
  loadFiles(page: number = 1, pageSize: number = this.pageSize): void {
    this.loading = true;
    this.error = '';
    this.pageSize = pageSize;
    this.filesService.getFiles(page, this.searchText, this.pageSize, this.selectedSort.sortBy, this.selectedSort.sortDirection).subscribe({
      next: (data) => {
        this.filePage = data;
        this.files = data.items ?? [];
        this.loading = false;
      },
      error: (err) => {
        this.error = getApiErrorMessage(err, 'Датотеките не може да се вчитаат.');
        console.error(err);
        this.loading = false;
      }
    });
  }
  onSort(option: SortOptionBase): void {
    this.selectedSort = option as FileSortOption;
    this.loadFiles();
  }
  onPageChange(event: PageEvent): void {
    this.loadFiles(event.pageIndex + 1, event.pageSize);
  }
  downloadFile(file: FileModel): void {
    if (!file.id) {
      this.error = 'Датотеката не може да се преземе.';
      return;
    }
    if (this.downloadingIds.has(file.id))
      return;
    this.error = '';
    this.downloadingIds.add(file.id);
    this.filesService.requestDownloadTicket(file.id).pipe(finalize(() => {
      this.downloadingIds.delete(file.id!);
    })).subscribe({
      next: ({ ticket }) => this.filesService.startBrowserDownload(ticket),
      error: (err) => {
        this.error = getApiErrorMessage(err, 'Датотеката не може да се преземе.');
        console.error(err);
      }
    });
  }
  isDownloading(fileId?: string): boolean {
    return !!fileId && this.downloadingIds.has(fileId);
  }
  getFileName(file: FileModel): string {
    return file.fileName ?? file.name ?? 'file';
  }
  getFileDateValue(file: FileModel): string | null {
    return file.dateAdded ?? file.createdAt ?? file.uploadedAt ?? null;
  }
  formatBytes(bytes?: number): string {
    if (!bytes || bytes <= 0)
      return '0 MB';
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  handleCreateCompleted(_file?: FileModel): void {
    this.loadFiles();
    this.hideModal();
  }
  handleCreateCancelled(): void {
    this.hideModal();
  }
  private hideModal(): void {
    const modalEl = this.createFileModal?.nativeElement;
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.hide();
  }
}
